import React, { useState, useEffect } from 'react';
import {
  RiHomeOfficeFill, RiExchangeLine, RiMoneyDollarCircleLine,
  RiBankCardLine, RiSendPlaneLine, RiArrowLeftLine, RiRefreshLine
} from 'react-icons/ri';
import './index.css';
import {
  getUser, formatCurrency, getCashStatus, sqlDateFormat
} from "../../../../utils/Common";
import TransactionStep from '../../../../components/ui/Reports/TransactionStep';
import CashCountStep from '../../../../components/ui/Reports/CashCountStep';
import CheckStep from '../../../../components/ui/Reports/CheckStep';
import { useCash } from '../../../../hooks/useCash';
import NotifyC from '../../../../components/Notifications';
import { useCashStatus } from "../../../../contexts/cash";
import Spinner from '../../../../components/Spinner';
import LockScreenSimple from '../../../../components/ui/LockScreenSimple';
import Swal from 'sweetalert2';
import { useRequests } from "../../../../hooks/useRequests";

const DailyReport = () => {
  const user = getUser();
  const { getCashBoxStatus, getDailyMovements, createDailyReport, getCashBoxMovements } = useCash();
  const { createRequest, searchRequests } = useRequests();
  const { setCashStatus } = useCashStatus();
  // Estados principales
  const [isLoading, setIsLoading] = useState(false);
  const [isTransactionLoading, setIsTransactionLoading] = useState(false);
  const [isCreatingReport, setIsCreatingReport] = useState(false);
  
  // Estados de datos
  const [hasApiTransactions, setHasApiTransactions] = useState(false);
  const [isCashOpen, setIsCashOpen] = useState(null); 
  const [isCashActive, setIsCashActive] = useState(null); 
  const [isReportCreated, setIsReportCreated] = useState(false);
  const [isCurrentPeriod, setIsCurrentPeriod] = useState(false);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [lockScreen, setLockScreen] = useState({ show: false, title: '', description: '' });
  const [openingCash, setOpeningCash] = useState(false);
  const [closingCash, setClosingCash] = useState(false);
  
  // Estados de UI
  const [showReportAlert, setShowReportAlert] = useState(true);
  const [currentView, setCurrentView] = useState('initial'); // 'initial', 'loading', 'locked', 'report'

  const [solicitudEnviada, setSolicitudEnviada] = useState(false);
  
  // Estados para solicitudes individuales
  const [reportRequests, setReportRequests] = useState([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [isUpdatingRequests, setIsUpdatingRequests] = useState(false);

  // Función simplificada para cargar solicitudes
  const loadReportRequests = async (fechaApertura, fechaCierre) => {
    setIsLoadingRequests(true);
    try {
      const dataToSend = {
        IdCaja: user.IdCaja,
        IdSolicitante: user.IdCliente,
        IdTipoSolicitud: '4', 
        FechaAperturaCaja: fechaApertura, 
        FechaCierreCaja: fechaCierre
      };
      
      const response = await searchRequests(null, dataToSend);
      
        if (response?.code === 200 && response.payload) {
         const filteredRequests = response.payload.filter(request => 
           request.status === 1 || request.status === 2 || request.status === 3 
         );
        setReportRequests(filteredRequests);
        processApprovedRequestsOnce(filteredRequests);
      } else {
        setReportRequests([]);
      }
    } catch (error) {
      console.error('Error al cargar solicitudes del informe:', error);
      setReportRequests([]);
    } finally {
      setIsLoadingRequests(false);
    }
  };



  // Función simplificada para procesar solicitudes aprobadas (solo una vez)
  const processApprovedRequestsOnce = (requests) => {
    const approvedRequests = requests.filter(request => request.status === 2);
    
    if (approvedRequests.length > 0) {
      setReportData(prev => {
        // Verificar si ya existen estas transacciones en el reporte
        const existingTransactionIds = prev.transactions.details
          .filter(t => t.requestId)
          .map(t => t.requestId);
        
        const newApprovedRequests = approvedRequests.filter(request => 
          !existingTransactionIds.includes(request.IdSolicitud)
        );
        
        if (newApprovedRequests.length > 0) {
          // Crear transacciones nuevas
          const newTransactions = newApprovedRequests.map(request => ({
            description: request.Comentarios || `Solicitud #${request.IdSolicitud}`,
            amount: request.SaldoIngresado,
            type: request.Accion === 3 ? 'debit' : 'credit',
            fromAPI: false,
            requestId: request.IdSolicitud
          }));
          
          // Calcular nuevos totales
          const newDebit = newTransactions
            .filter(t => t.type === 'debit')
            .reduce((sum, t) => sum + t.amount, prev.transactions.debit);
          const newCredit = newTransactions
            .filter(t => t.type === 'credit')
            .reduce((sum, t) => sum + t.amount, prev.transactions.credit);
          
          return {
            ...prev,
            transactions: {
              details: [...prev.transactions.details, ...newTransactions],
              debit: newDebit,
              credit: newCredit
            }
          };
        }
        
        return prev;
      });
    }
  };

  // Función para cargar datos desde sessionStorage
  const loadFromSessionStorage = (key, defaultValue) => {
    try {
      const saved = sessionStorage.getItem(key);
      return saved ? JSON.parse(saved) : defaultValue;
    } catch (error) {
      console.error('Error loading from sessionStorage:', error);
      return defaultValue;
    }
  };

  // Función para actualizar solicitudes del sessionStorage con nuevos comentarios
  const updateSessionStorageRequests = async () => {
    setIsUpdatingRequests(true);
    try {
      await loadReportRequests(openingCash, closingCash);
      NotifyC.success("Solicitudes actualizadas correctamente desde el servidor.");
    } catch (error) {
      NotifyC.error("Error al actualizar las solicitudes. Inténtalo de nuevo.");
    } finally {
      setIsUpdatingRequests(false);
    }
  };

  // Estado del reporte
  const [reportData, setReportData] = useState(() =>
    loadFromSessionStorage('dailyReport', {
      saldoInicialCaja: 0,
      transactions: { details: [], debit: 0, credit: 0 },
      cashCount: {
        bills: [
          { denomination: 100, quantity: 0, total: 0 },
          { denomination: 50, quantity: 0, total: 0 },
          { denomination: 20, quantity: 0, total: 0 },
          { denomination: 10, quantity: 0, total: 0 },
          { denomination: 5, quantity: 0, total: 0 },
          { denomination: 1, quantity: 0, total: 0 }
        ],
        coins: [
          { denomination: 1, quantity: 0, total: 0 },
          { denomination: 0.5, quantity: 0, total: 0 },
          { denomination: 0.25, quantity: 0, total: 0 },
          { denomination: 0.1, quantity: 0, total: 0 },
          { denomination: 0.05, quantity: 0, total: 0 },
          { denomination: 0.01, quantity: 0, total: 0 }
        ],
        totalBills: 0,
        totalCoins: 0,
        grandTotal: 0
      },
      checks: [],
      currentStep: 1
    })
  );

  const [newTransaction, setNewTransaction] = useState({
    showInput: false, description: '', amount: '', type: 'debit'
  });

  const [newCheck, setNewCheck] = useState({
    number: '', date: '', bank: '', drawer: '', beneficiary: '', amount: ''
  });

  const resetReportData = () => {
      sessionStorage.removeItem('dailyReport');
      sessionStorage.removeItem('pendingTransaction');
      sessionStorage.removeItem('pendingDeleteTransaction');
      setReportData({
        saldoInicialCaja: 0,
        transactions: { details: [], debit: 0, credit: 0 },
        cashCount: {
          bills: reportData.cashCount.bills.map(b => ({ ...b, quantity: 0, total: 0 })),
          coins: reportData.cashCount.coins.map(c => ({ ...c, quantity: 0, total: 0 })),
          totalBills: 0,
          totalCoins: 0,
          grandTotal: 0
        },
        checks: [],
        currentStep: 1
      });
  };

  const evaluateSystemStatus = () => {
   if (isReportCreated) {
      return {
        view: 'locked',
        message: {
          type: 'warning',
          title: 'Informe ya creado',
          description: 'Ya se ha generado un informe para el último cierre/apertura registrado.',
          show: true
        }
      };
    } else if (reportData.transactions.details.length === 0) {
      return {
        view: 'locked',
        message: {
          type: 'warning',
          title: 'No hay movimientos',
          description: 'No se registraron giros en el turno anterior.',
          show: true
        }
      };
    } else {
      return {
        view: 'report',
        message: { type: 'info', title: '', description: '', show: false }
      };
    }
  };

  const handleAddTransaction = async () => {
    if (!newTransaction.description || !newTransaction.amount) return;
    
    // Verificar que las fechas de apertura y cierre estén disponibles
    if (!openingCash || !closingCash) {
      NotifyC.error("No se puede crear la solicitud. Las fechas de apertura y cierre de caja no están disponibles.");
      return;
    }
    
    // Verificar si hay solicitudes pendientes
    if (hasPendingRequests()) {
      NotifyC.error("Tienes una solicitud pendiente de aprobación. No puedes agregar más transacciones hasta que sea aprobada.");
      return;
    }
    
    setIsTransactionLoading(true);
    const amount = parseFloat(newTransaction.amount) || 0;
    const newDetail = {
      description: newTransaction.description,
      amount,
      type: newTransaction.type,
      fromAPI: false
    };
    
         // Crear solicitud automáticamente para esta transacción
     try {
       const dataToSend = {
         IdTipoSolicitud: 4,
         IdEstatus: 1,
         IdCaja: user.IdCaja,
         IdSucursal: user.IdEstafeta,
         Accion: newTransaction.type === 'debit' ? 3 : 4, // 3 = agregar, 4 = retirar
         SaldoIngresado: amount,
         IdProvincia: user.IdProvincia,
         Comentarios: newTransaction.description,
         FechaAperturaCaja: openingCash, // Asignar período de caja
         FechaCierreCaja: closingCash
       };
      
      const response = await createRequest(null, dataToSend);

      const errorMessages = {
        "ACTION_TYPE_INVALID": "Tipo de acción inválido.",
        "CLIENT_NOT_FOUND_IN_SYSTEM": "Usuario no encontrado en el sistema.",
        "OFFICE_NOT_FOUND": "Estafeta no encontrada.",
        "OFFICE_NOT_ACTIVE": "La estafeta de la caja se encuentra desactivada. Por favor, contacta al administrador para activar la estafeta.",
        "CASHBOX_NOT_FOUND": "Caja no encontrada.",
        "CASHBOX_NOT_IN_OFFICE": "La caja no pertenece a la estafeta.",
        "ERROR_CREATING_REQUEST": "Error al crear la solicitud.",
        "CASHBOX_HAS_PENDING_REQUEST": "La caja tiene una solicitud pendiente.",
        "CASHBOX_NOT_ACTIVE": "La caja se encuentra desactivada. Por favor, contacta al administrador para activar la caja.",
    };

    const successMessages = {
      3: "Tu solicitud ha sido enviada para añadir el monto.",
      4: "Tu solicitud ha sido enviada para retirar el monto.",
      5: "Tu solicitud ha sido enviada para anular el monto.",
    };
      
    if (response?.code === 200) {
         // Guardar la transacción en sessionStorage hasta que sea aprobada
         sessionStorage.setItem('pendingTransaction', JSON.stringify(newDetail));
         setSolicitudEnviada(true);
         
         // Actualizar inmediatamente el estado de solicitudes para mostrar la nueva solicitud
         const newRequest = {
           IdSolicitud: response.payload?.IdSolicitud || Date.now(), // Usar ID del response o timestamp como fallback
           status: 1, // Pendiente
           Accion: newTransaction.type === 'debit' ? 3 : 4,
           SaldoIngresado: amount,
           Comentarios: newTransaction.description,
           FechaCreacion: new Date().toISOString(),
           NombreSolicitante: user.Nombre || 'N/A'
         };
         
         setReportRequests(prev => [...prev, newRequest]);
         
         Swal.fire({
           title: '¡Solicitud Creada!',
           text: successMessages[newTransaction.type === 'debit' ? 3 : 4] || 'Solicitud completada.',
           icon: 'info',
           confirmButtonText: 'Entendido',
           customClass: { confirmButton: 'custom-confirm-button' }
         });
      } else {
        Swal.fire({
          title: 'Error al Crear Solicitud',
          text: errorMessages[response.message] || 'No se pudo crear la solicitud. La transacción no se agregó.',
          icon: 'error',
          confirmButtonText: 'Entendido',
          customClass: { confirmButton: 'custom-confirm-button' }
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error al Crear Solicitud',
        text: 'Ocurrió un error al crear la solicitud. La transacción no se agregó.',
        icon: 'error',
        confirmButtonText: 'Entendido',
        customClass: { confirmButton: 'custom-confirm-button' }
      });
    }
    
    setNewTransaction({
      showInput: false,
      description: '',
      amount: '',
      type: 'debit'
    });
    setTimeout(() => setIsTransactionLoading(false), 500);
  };

  // Esta funcion se deshabilita temporalmente
  /*
  const handleDeleteTransaction = async (index) => {

    const { canProceed } = await handleRequestStatus(false);
    if (!canProceed) {
      NotifyC.error("Tienes una solicitud pendiente de aprobación. No puedes eliminar transacciones hasta que sea aprobada.");
      return;
    }

    setIsTransactionLoading(true);
    const transactionToDelete = reportData.transactions.details[index];
    const { type, amount } = transactionToDelete;
    const accion = type === 'debit' ? 4 : 3;  
    const dataToSend = {
      IdTipoSolicitud: 2,
      IdEstatus: 1,
      IdCaja: user.IdCaja,
      IdSucursal: user.IdEstafeta,
      Accion: accion,
      SaldoIngresado: amount,
      IdProvincia: user.IdProvincia,
    };

    try {
      const response = await createRequest(null, dataToSend);

      const errorMessages = {
        "ACTION_TYPE_INVALID": "Tipo de acción inválido.",
        "CLIENT_NOT_FOUND_IN_SYSTEM": "Usuario no encontrado en el sistema.",
        "OFFICE_NOT_FOUND": "Sucursal no encontrada.",
        "CASHBOX_NOT_FOUND": "Caja no encontrada.",
        "CASHBOX_NOT_IN_OFFICE": "La caja no pertenece a la sucursal.",
        "ERROR_CREATING_REQUEST": "Error al crear la solicitud.",
        "CASHBOX_HAS_PENDING_REQUEST": "La caja tiene una solicitud pendiente.",
      };
      const successMessages = {
        3: "Solicitud enviada para reponer el saldo eliminado.",
        4: "Solicitud enviada para retirar el saldo eliminado.",
      };

      if (response?.code === 200) {
        setSolicitudEnviada(true);
        Swal.fire({
          title: '¡Solicitud Creada!',
          text: successMessages[accion] || 'Solicitud completada.',
          icon: 'info',
          confirmButtonText: 'Entendido',
          customClass: { confirmButton: 'custom-confirm-button' }
        });


        sessionStorage.setItem('pendingDeleteTransaction', JSON.stringify({ index, transactionToDelete }));
      } else {
        Swal.fire({
          title: 'Error al Crear Solicitud',
          text: errorMessages[response?.message] || 'No se pudo crear la solicitud. La transacción no se eliminó.',
          icon: 'error',
          confirmButtonText: 'Entendido',
          customClass: { confirmButton: 'custom-confirm-button' }
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error al Crear Solicitud',
        text: 'Ocurrió un error al crear la solicitud. La transacción no se eliminó.',
        icon: 'error',
        confirmButtonText: 'Entendido',
        customClass: { confirmButton: 'custom-confirm-button' }
      });
    }
    setTimeout(() => setIsTransactionLoading(false), 500);
  };
  */

  const handleCashQuantityChange = (type, index, value) => {
    const newValue = parseInt(value) || 0;
    const updatedCashCount = { ...reportData.cashCount };
    if (type === 'bills') {
      updatedCashCount.bills[index].quantity = newValue;
      updatedCashCount.bills[index].total = updatedCashCount.bills[index].denomination * newValue;
      updatedCashCount.totalBills = updatedCashCount.bills.reduce((sum, bill) => sum + bill.total, 0);
    } else {
      updatedCashCount.coins[index].quantity = newValue;
      updatedCashCount.coins[index].total = updatedCashCount.coins[index].denomination * newValue;
      updatedCashCount.totalCoins = updatedCashCount.coins.reduce((sum, coin) => sum + coin.total, 0);
    }
    updatedCashCount.grandTotal = updatedCashCount.totalBills + updatedCashCount.totalCoins;
    setReportData(prev => ({
      ...prev,
      cashCount: updatedCashCount
    }));
  };

  const handleAddCheck = () => {
    if (!newCheck.number || !newCheck.amount) return;
    const amount = parseFloat(newCheck.amount) || 0;
    const checkData = {
      number: newCheck.number,
      date: newCheck.date || new Date().toISOString().split('T')[0],
      bank: newCheck.bank,
      drawer: newCheck.drawer,
      beneficiary: newCheck.beneficiary,
      amount
    };
    setReportData(prev => ({
      ...prev,
      checks: [...prev.checks, checkData]
    }));
    setNewCheck({
      number: '',
      date: '',
      bank: '',
      drawer: '',
      beneficiary: '',
      amount: ''
    });
  };

  const handleDeleteCheck = (index) => {
    const updatedChecks = [...reportData.checks];
    updatedChecks.splice(index, 1);
    setReportData(prev => ({
      ...prev,
      checks: updatedChecks
    }));
  };

  const nextStep = () => {
    setReportData(prev => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, 4)
    }));
  };

  const prevStep = () => {
    setReportData(prev => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 1)
    }));
  };

  const handleErrorResponse = (response) => {
    if (response.code !== 200){
      switch (response.message){
        case 'NO_OPENING_MOVEMENT_FOUND':
          NotifyC.error('No se encontró un movimiento de apertura para el turno actual.');
          break;
        case 'NO_CLOSING_MOVEMENT_FOUND':
          NotifyC.error('No se encontró un movimiento de cierre para el turno actual.');
          break;
        case 'CLIENT_REPORT_ALREADY_EXISTS_FOR_THIS_PERIOD':
          NotifyC.error('Ya existe un informe diario para el turno actual.');
          break;
        default:
          NotifyC.error('Error al realizar acción');
          break;
      }
    }
  };

  const handleSubmit = async () => {
    if (isCreatingReport) return; 

    if (isCashActive === true) {
      NotifyC.error('La caja está desactivada. No se puede crear el informe diario.');
      return;
    }

    if (isCashOpen === true) {
      NotifyC.error('La caja está abierta. No se puede crear el informe diario.');
      return;
    }

    if (isReportCreated === true) {
      NotifyC.error('Ya se ha creado un informe diario para el turno actual.');
      return;
    }

    const detallesApi = reportData.transactions.details.filter(d => d.fromAPI);
 
    const detallesLocales = reportData.transactions.details.filter(d => !d.fromAPI);

    const totalDebitApi = detallesApi.filter(d => d.type === "debit").reduce((sum, d) => sum + d.amount, 0);
    const totalCreditApi = detallesApi.filter(d => d.type === "credit").reduce((sum, d) => sum + d.amount, 0);

    const totalDebitLocal = detallesLocales.filter(d => d.type === "debit").reduce((sum, d) => sum + d.amount, 0);
    const totalCreditLocal = detallesLocales.filter(d => d.type === "credit").reduce((sum, d) => sum + d.amount, 0);

    const dataToSend = {
      ...reportData,
      fecha: new Date().toISOString(),
      IdCaja: user.IdCaja,
      IdEstafeta: user.IdEstafeta,
      transactions: {
        ...reportData.transactions,
        details: reportData.transactions.details.map(d => ({
          ...d,
          type: d.type === "debit" ? 1 : 2
        }))
      },
      cashCount: {
        bills: reportData.cashCount.bills
          .filter(b => b.total > 0)
          .map(b => ({
            ...b,
            TypeCash: 1
          })),
        coins: reportData.cashCount.coins
          .filter(c => c.total > 0)
          .map(c => ({
            ...c,
            TypeCash: 2
          })),
        totalBills: reportData.cashCount.totalBills,
        totalCoins: reportData.cashCount.totalCoins,
        grandTotal: reportData.cashCount.grandTotal
      },
      summary: {
        totalDebitApi: totalDebitApi,
        totalCreditApi: totalCreditApi,
        totalDebitLocal: totalDebitLocal,
        totalCreditLocal: totalCreditLocal,
        checksTotal: reportData.checks.reduce((sum, check) => sum + check.amount, 0),
        cashTotal: reportData.cashCount.grandTotal,
        grandTotal: (reportData.cashCount.grandTotal + reportData.checks.reduce((sum, check) => sum + check.amount, 0)),
      }
    };

    setIsCreatingReport(true);

    const confirm = await Swal.fire({
      title: 'Confirmar Creación de Informe',
      text: '¿Estás seguro de que deseas crear el informe diario con los datos ingresados?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Crear Informe',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'custom-confirm-button',
        cancelButton: 'custom-cancel-button'
      }
    });

    if (!confirm.isConfirmed) {
      setIsCreatingReport(false);
      return;
    }
    
    const abortController = new AbortController();
    try {
      const response = await createDailyReport(abortController.signal, dataToSend);
      if(response?.code === 200){
        Swal.fire({
            title: '¡Informe creado!',
            text: 'El informe diario se ha creado correctamente.',
            icon: "success",
            confirmButtonText: "Ok",
            customClass: { confirmButton: 'custom-confirm-button' }
        });
      
        resetReportData();
        setIsCreatingReport(false);
        setIsReportCreated(true); 
      } else {
        handleErrorResponse(response);
        setIsCreatingReport(false);
        setIsReportCreated(false);
      }
    }catch (error){
      NotifyC.error("Ocurrió un error inesperado");
    }
  };


  // Función simplificada para verificar si hay solicitudes pendientes
  const hasPendingRequests = () => {
    return reportRequests.some(request => request.status === 1);
  };

  // Función para renderizar las solicitudes individuales
  const renderReportRequests = () => {
    if (isLoadingRequests) {
      return (
        <div className="text-center p-3">
          <Spinner type={2} size={24} />
          <p>Cargando solicitudes...</p>
        </div>
      );
    }

    if (reportRequests.length === 0) {
      return (
        <div className="text-center p-3 text-muted">
          <p>No hay solicitudes de sobrante/faltante para este turno.</p>
        </div>
      );
    }

    return (
      <div className="report-requests-section">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">
            Solicitudes de Sobrante/Faltante
            {isLoadingRequests && (
              <span className="ml-2 text-muted" style={{ fontSize: '0.8rem' }}>
                <Spinner type={2} size={12} /> Cargando...
              </span>
            )}
          </h4>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={updateSessionStorageRequests}
            disabled={isLoadingRequests || isUpdatingRequests}
            style={{
              fontSize: '0.8rem',
              padding: '4px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            {isUpdatingRequests ? (
              <Spinner type={2} size={12} />
            ) : (
              <RiRefreshLine size={14} />
            )}
            {isUpdatingRequests ? 'Actualizando...' : 'Actualizar'}
          </button>
        </div>
        <div className="requests-grid">
          {reportRequests.map((request, index) => {
            // Determinar el tipo de movimiento y color del estado
            const tipoMovimiento = request.Accion === 3 ? 'DEBITO' : 'CREDITO';
            const estadoColor = request.status === 2 ? 'success' : request.status === 1 ? 'warning' : 'danger';
            const estadoTexto = request.status === 2 ? 'Aprobada' : request.status === 1 ? 'Pendiente' : 'Rechazada';
            
            return (
              <div key={request.IdSolicitud} className={`request-card ${estadoColor}`}>
                <div className="request-header">
                  <span className="request-id">#{request.IdSolicitud}</span>
                  <span className={`request-status ${estadoColor}`}>
                    {estadoTexto}
                  </span>
                </div>
                <div className="request-body">
                  <div className="request-type">
                    <strong>{tipoMovimiento}</strong>
                  </div>
                  <div className="request-amount">
                    {formatCurrency(request.SaldoIngresado)}
                  </div>
                  {request.Comentarios && (
                    <div className="request-comments">
                      <small>{request.Comentarios}</small>
                    </div>
                  )}
                  <div className="request-dates">
                    <small>
                      <strong>Creada:</strong> {sqlDateFormat(request.FechaCreacion)}
                    </small>
                    {request.FechaRevision && (
                      <small>
                        <strong>Revisada:</strong> {sqlDateFormat(request.FechaRevision)}
                      </small>
                    )}
                  </div>
                  <div className="request-user">
                    <small>
                      <strong>Solicitante:</strong> {request.NombreSolicitante || 'N/A'}
                    </small>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderStep = () => {
    switch (reportData.currentStep) {
      case 1:
        return (
          <div>
            <TransactionStep
              nameCashbox={user.IdCaja}
              reportData={reportData}
              newTransaction={newTransaction}
              setNewTransaction={setNewTransaction}
              handleAddTransaction={handleAddTransaction}
              //handleDeleteTransaction={handleDeleteTransaction}
              isTransactionLoading={isTransactionLoading}
              nextStep={nextStepIntercept}
            />
            {/* Sección de solicitudes individuales */}
            <div className="mt-4">
              {renderReportRequests()}
            </div>
          </div>
        );
      case 2:
        return (
          <CashCountStep
            cashCount={reportData.cashCount}
            handleCashQuantityChange={handleCashQuantityChange}
            prevStep={prevStep}
            nextStep={nextStep}
          />
        );
      case 3:
        return (
          <CheckStep
            checks={reportData.checks}
            newCheck={newCheck}
            setNewCheck={setNewCheck}
            handleAddCheck={handleAddCheck}
            handleDeleteCheck={handleDeleteCheck}
            prevStep={prevStep}
            nextStep={nextStep}
          />
        );
      case 4:
        const checksTotal = reportData.checks.reduce((sum, check) => sum + check.amount, 0);
        const grandTotal = reportData.cashCount.grandTotal + checksTotal;
        const saldoInicial = reportData.saldoInicialCaja || 0;
        const totalIngresos = reportData.transactions.debit;
        const totalEgresos = reportData.transactions.credit;
        const saldoSegunOperaciones = saldoInicial + totalIngresos - totalEgresos;
        const balanceOperacionesIngreso = saldoInicial + totalIngresos;
        const balanceOperacionesEgreso = totalEgresos + saldoSegunOperaciones;
        
        const arqueoValido =
          Math.abs(grandTotal - saldoSegunOperaciones) < 0.01;

        return (
          <div className="report-review">
            {/* Header */}
            <div className="panelHeader">
              <h3>RESÚMEN DE INFORME</h3>
              <div className="dateDisplay">
                {new Date().toLocaleDateString('es-ES', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>
            {/* Main Content */}
            <div className="report-grid">
              {/* Operaciones */}
              <div className="report-card operations-card">
                <div className="card-header">
                  <RiExchangeLine className="card-icon" />
                  <h3>Movimientos</h3>
                </div>
                <div className="card-body">
                  <div className="data-row">
                    <span className="data-label">Saldo Inicial:</span>
                    <span className="data-value neutral">
                      {saldoInicial.toFixed(2)}
                    </span>
                  </div>
                  <div className="data-row">
                    <span className="data-label">Ingresos (Débitos):</span>
                    <span className="data-value positive">
                      {totalIngresos.toFixed(2)}
                    </span>
                  </div>
                  <div className="data-row">
                    <span className="data-label">Egresos (Créditos):</span>
                    <span className="data-value negative">
                      {totalEgresos.toFixed(2)}
                    </span>
                  </div>
                  <div className="data-divider"></div>
                  <div className="data-row emphasized">
                    <span className="data-label">Saldo Operacional:</span>
                    <span className="data-value neutral">
                      {saldoSegunOperaciones.toFixed(2)}
                    </span>
                  </div>
                  <div className="data-row">
                    <span className="data-label">Balance Ingresos:</span>
                    <span className="data-value neutral">
                      {balanceOperacionesIngreso.toFixed(2)}
                    </span>
                  </div>
                  <div className="data-row">
                    <span className="data-label">Balance Egresos:</span>
                    <span className="data-value neutral">
                      {balanceOperacionesEgreso.toFixed(2)}
                    </span>
                  </div>
                  <div className="data-meta">
                    <span>Total Transacciones: {reportData.transactions.details.length}</span>
                  </div>
                </div>
              </div>
              {/* Efectivo */}
              <div className="report-card cash-card">
                <div className="card-header">
                  <RiMoneyDollarCircleLine className="card-icon" />
                  <h3>Arqueo de Efectivo</h3>
                </div>
                <div className="card-body">
                  <div className="data-row">
                    <span className="data-label">Total Billetes:</span>
                    <span className="data-value positive">
                      {reportData.cashCount.totalBills.toFixed(2)}
                    </span>
                  </div>
                 <div className="data-row">
                    <span className="data-label">Total Monedas:</span>
                    <span className="data-value positive">
                      {reportData.cashCount.totalCoins.toFixed(2)}
                    </span>
                  </div>
                  <div className="data-divider"></div>
                  <div className="data-row">
                    <span className="data-label">Total Efectivo:</span>
                    <span className="data-value positive">
                      {reportData.cashCount.grandTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              {/* Cheques */}
              <div className="report-card checks-card">
                <div className="card-header">
                  <RiBankCardLine className="card-icon" />
                  <h3>Gestión de Cheques</h3>
                </div>
                <div className="card-body">
                  <div className="data-row">
                    <span className="data-label">Total Cheques:</span>
                    <span className="data-value neutral">
                      {checksTotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="data-meta">
                    <span>Cantidad: {reportData.checks.length}</span>
                  </div>
                </div>
              </div>
            </div>
            {/* Validation Message */}
            <div style={{ textAlign: 'center', marginBottom: '10px', color: arqueoValido ? 'green' : 'red' }}>
              {arqueoValido
                ? 'El total de arqueo y cheques coincide con el saldo según operaciones.'
                : 'El total de arqueo y cheques no coincide con el saldo según operaciones.'}
            </div>
            {/* Footer */}
            <div className="report-footer">
              <button
                className="confirm-btn"
                onClick={handleSubmit}
                disabled={!hasApiTransactions || reportData.transactions.details.length === 0 || isLoading || !arqueoValido }
              >
                <RiSendPlaneLine className="btn-icon" />
                {isCreatingReport ? <Spinner type={2} size={18} /> : 'Crear Informe'}
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const handleGetCashBoxStatus = async () => {
    const abortController = new AbortController();
    const response = await getCashBoxStatus(abortController.signal);
    let estatusCajaUsuario = null;
    let isCashActive = null;
    if (response?.cajas && user?.IdCaja) {
      const cajaEncontrada = response.cajas.find(caja => caja.IdCaja === user.IdCaja);
      estatusCajaUsuario = cajaEncontrada ? cajaEncontrada.EstatusCaja : null;
      isCashActive = cajaEncontrada ? cajaEncontrada.IsActive : null;
    }
    setIsCashOpen(estatusCajaUsuario === 1); // true = abierta, false = cerrada
    setIsCashActive(isCashActive === 2);
    setCashStatus(getCashStatus(estatusCajaUsuario));
  };

  const loadMovements = async () => {
    setIsLoading(true);
    setCurrentView('loading');
    try {
      const today = new Date();
      const localDate = today.getFullYear() + '-' +
        String(today.getMonth() + 1).padStart(2, '0') + '-' +
        String(today.getDate()).padStart(2, '0');
      const dataToSend = { fecha: localDate };
      const abortController = new AbortController();
      const apiMovements = await getDailyMovements(abortController.signal, dataToSend);

      if (apiMovements?.code === 200 && apiMovements.payload) {
        const p = apiMovements.payload[0];
        setIsCurrentPeriod(p.TurnoVigente);
        setIsReportCreated(p.ExisteReporte);
        setOpeningCash(p.AperturaTurno);
        setClosingCash(p.CierreTurno);
        
        // Cargar solicitudes individuales del informe (solo una vez al inicio)
        if (p.AperturaTurno) {
          await loadReportRequests(p.AperturaTurno, p.CierreTurno);
        }
        const mappedMovements = [
          { description: 'VENTA DE GIRO POSTAL', amount: parseFloat(p.VentaGiroPostal) || 0, type: 'debit', fromAPI: true },
          { description: 'VENTA DE GIRO TELEGRAFICO', amount: parseFloat(p.VentaGiroTelegrafico) || 0, type: 'debit', fromAPI: true },
          { description: 'PAGO DE GIRO POSTAL', amount: parseFloat(p.PagoGiroPostal) || 0, type: 'credit', fromAPI: true },
          { description: 'PAGO DE GIRO TELEGRAFICO', amount: parseFloat(p.PagoGiroTelegrafico) || 0, type: 'credit', fromAPI: true },
          { description: 'REINTEGRO DE GIRO POSTAL', amount: parseFloat(p.ReintegroGiroPostal) || 0, type: 'credit', fromAPI: true },
          { description: 'REINTEGRO DE GIRO TELEGRAFICO', amount: parseFloat(p.ReintegroGiroTelegrafico) || 0, type: 'credit', fromAPI: true },
          { description: 'COMISIÓN DE GIRO POSTAL', amount: parseFloat(p.ComisionGiroPostal) || 0, type: 'debit', fromAPI: true },
          { description: 'COMISIÓN DE GIRO TELEGRAFICO', amount: parseFloat(p.ComisionGiroTelegrafico) || 0, type: 'debit', fromAPI: true },
          { description: 'COMISIÓN TRANS GIRO POSTAL', amount: parseFloat(p.ComisionTransGiroPostal) || 0, type: 'debit', fromAPI: true },
          { description: 'COMISIÓN TRANS GIRO TELEGRAFICO', amount: parseFloat(p.ComisionTransGiroTelegrafico) || 0, type: 'debit', fromAPI: true },
          { description: 'PAGO DE GIRO POSTAL EXPIRADO', amount: parseFloat(p.PagoExpiradoGiroPostal) || 0, type: 'credit', fromAPI: true },
          { description: 'PAGO DE GIRO TELEGRAFICO EXPIRADO', amount: parseFloat(p.PagoExpiradoGiroTelegrafico) || 0, type: 'credit', fromAPI: true },
          { description: 'REINTEGRO DE GIRO POSTAL EXPIRADO', amount: parseFloat(p.ReintegroExpiradoGiroPostal) || 0, type: 'credit', fromAPI: true },
          { description: 'REINTEGRO DE GIRO TELEGRAFICO EXPIRADO', amount: parseFloat(p.ReintegroExpiradoGiroTelegrafico) || 0, type: 'credit', fromAPI: true },
        ].filter(m => m.amount > 0);
        setHasApiTransactions(mappedMovements.length > 0);
        setReportData(prev => {
          const localDetails = prev.transactions.details.filter(d => !d.fromAPI);
          const localDebit = localDetails.filter(d => d.type === 'debit').reduce((sum, d) => sum + d.amount, 0);
          const localCredit = localDetails.filter(d => d.type === 'credit').reduce((sum, d) => sum + d.amount, 0);
          const apiDebit = mappedMovements.filter(m => m.type === 'debit').reduce((sum, m) => sum + m.amount, 0);
          const apiCredit = mappedMovements.filter(m => m.type === 'credit').reduce((sum, m) => sum + m.amount, 0);
          return {
            ...prev,
            saldoInicialCaja: p.SaldoInicial || 0,
            transactions: {
              details: [...mappedMovements, ...localDetails],
              debit: apiDebit + localDebit,
              credit: apiCredit + localCredit
            }
          };
        });
        setHasLoadedData(true);
        setCurrentView('report');
      } else {
        setLockScreen({
          show: true,
          title: 'Error',
          description: 'No se encontraron los movimientos del día'
        });
        setCurrentView('locked');
      }
    } catch (err) {
      setLockScreen({
        show: true,
        title: 'Error',
        description: 'Error al cargar los movimientos del dia'
      });
      setCurrentView('locked');
    } finally {
      setIsLoading(false);
    }
  };

  const nextStepIntercept = () => {
    if (reportData.currentStep !== 1) {
      nextStep();
      return;
    }
    // Verificar si hay solicitudes pendientes - si las hay, no permitir avanzar
    if (hasPendingRequests()) {
      NotifyC.error("Tienes solicitudes pendientes de aprobación. No puedes avanzar hasta que todas las solicitudes sean aprobadas o rechazadas.");
      return;
    }
    nextStep();
  };
  


  useEffect(() => {
    const mainFlow = async () => {
      setIsLoading(true);
      setCurrentView('loading');
      await handleGetCashBoxStatus();
      setTimeout(async () => {
        if (isCashOpen === null) return;
        if (isCashActive) {
          setLockScreen({
            show: true,
            title: 'Caja desactivada',
            description: 'La caja está desactivada. No se puede crear el informe diario.'
          });
          setCurrentView('locked');
          setIsLoading(false);
          return;
        }
        if (isCashOpen) {
          setLockScreen({
            show: true,
            title: 'Caja abierta',
            description: 'No puedes generar el informe mientras la caja esté abierta. Por favor, cierra la caja para continuar.'
          });
          setCurrentView('locked');
          setIsLoading(false);
        } else {
          // Caja cerrada: busca movimientos
          await loadMovements();
        }
      }, 0);
    };
    mainFlow();
  }, [isCashOpen, isCashActive]);

  useEffect(() => {
    if (isCashOpen === false && hasLoadedData) {
      const status = evaluateSystemStatus();
      if (status.view === 'locked') {
        setLockScreen({
          show: true,
          title: status.message.title,
          description: status.message.description
        });
        setCurrentView('locked');
      } else {
        setLockScreen({ show: false, title: '', description: '' });
        setCurrentView(status.view);
      }
    }
  }, [
    isCashOpen,
    isReportCreated,
    reportData.saldoInicialCaja,
    reportData.transactions.details.length,
    hasLoadedData,
    isCurrentPeriod,
    user.roleId,
    currentView
  ]);

  useEffect(() => {
    sessionStorage.setItem('dailyReport', JSON.stringify(reportData));
  }, [reportData]);



  return (
    <>
      <div className="dashboardCard text-left" style={{ color: "black", marginBottom: "20px" }}>
        <div className="d-flex justify-content-between" style={{ gap: 20 }}>
          <div className="d-flex align-items-center">
            <RiHomeOfficeFill size={20} className="mr-2" />
            <h5 className="mb-0">Crear informe</h5>
          </div>
        </div>
      </div>

      {/* Alerta informativa */}
      {showReportAlert && currentView !== 'loading' && (
        <div className="dashboardCard p-0">
          <div className="alert alert-warning"
            style={{
              marginBottom: 20,
              fontWeight: 500,
              background: "#fffbe6",
              border: "1px solid #ffe58f",
              color: "#ad8b00",
              position: "relative",
            }}
          >
            <button
              type="button"
              onClick={() => setShowReportAlert(false)}
              style={{
                position: "absolute",
                top: 8,
                right: 12,
                background: "transparent",
                border: "none",
                fontSize: 18,
                fontWeight: "bold",
                color: "#ad8b00",
                cursor: "pointer",
                lineHeight: 1,
              }}
              aria-label="Cerrar"
            >
              ×
            </button>
            Recuerda: Este informe solo puede genearse una vez la caja este cerrada, contando unicamente con los movimientos de ese turno. (Apertura y Cierre)
          </div>
        </div>
      )}

      {/* Renderizado condicional según el estado */}
      {isLoading && currentView === 'loading' ? (
        <LockScreenSimple 
          isLocked={true} 
          title="Cargando..." 
          description="Obteniendo información de la caja y movimientos diarios..." 
          isLoading={isLoading} 
        />
      ) : currentView === 'locked' ? (
        <>
            <LockScreenSimple 
              isLocked={lockScreen.show} 
              title={lockScreen.title} 
              description={lockScreen.description} 
            />    
        </>
      ) : currentView === 'report' ? (
        <>
          <div className="dashboardCard p-3">
            <div className="timeline-card">
              <div className="timeline-steps mb-3">
                <div
                  className={`timeline-step ${reportData.currentStep >= 1 ? 'active' : ''}`}
                  onClick={() => setReportData(prev => ({ ...prev, currentStep: 1 }))}
                >
                  <div className="step-indicator"></div>
                  <span>Transacciones</span>
                </div>
                <div className={`timeline-connector ${reportData.currentStep >= 2 ? 'active' : ''}`}></div>
                <div
                  className={`timeline-step ${reportData.currentStep >= 2 ? 'active' : ''}`}
                  onClick={() => reportData.currentStep > 1 && setReportData(prev => ({ ...prev, currentStep: 2 }))}
                >
                  <div className="step-indicator"></div>
                  <span>Efectivo</span>
                </div>
                <div className={`timeline-connector ${reportData.currentStep >= 3 ? 'active' : ''}`}></div>
                <div
                  className={`timeline-step ${reportData.currentStep >= 3 ? 'active' : ''}`}
                  onClick={() => reportData.currentStep > 2 && setReportData(prev => ({ ...prev, currentStep: 3 }))}
                >
                  <div className="step-indicator"></div>
                  <span>Cheques</span>
                </div>
                <div className={`timeline-connector ${reportData.currentStep >= 4 ? 'active' : ''}`}></div>
                <div
                  className={`timeline-step ${reportData.currentStep >= 4 ? 'active' : ''}`}
                  onClick={() => reportData.currentStep > 3 && setReportData(prev => ({ ...prev, currentStep: 4 }))}
                >
                  <div className="step-indicator"></div>
                  <span>Revisión</span>
                </div>
              </div>
              <div className="summary-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                {reportData.currentStep > 1 && (
                  <button className="btn btn-outline-blue" onClick={prevStep}>
                    <RiArrowLeftLine />ANTERIOR
                  </button>
                )}
                                 {reportData.currentStep < 4 && (
                   <button
                     className="btn btn-blue"
                     onClick={nextStepIntercept}
                     disabled={
                       (reportData.currentStep === 1 && (!hasApiTransactions || reportData.transactions.details.length === 0)) ||
                       (reportData.currentStep === 1 && hasPendingRequests())
                     }
                   >
                     <RiSendPlaneLine /> SIGUIENTE
                   </button>
                 )}

              </div>
            </div>
            <div className="reportPanel">
              {renderStep()}
            </div>
          </div>
        </>
      ) : null}
    </>
  );
};

export default DailyReport;